import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { PageBody } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import { toast } from "sonner";
import {
    Sparkle, ArrowsClockwise, Pulse, Quotes, ArrowLeft, GearSix,
    TrendUp, TrendDown, Trash, Pause, Play,
} from "@phosphor-icons/react";
import {
    LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Area, AreaChart,
} from "recharts";
import EditableChips from "../components/EditableChips";
import { StreamErrorBanner } from "../components/StreamErrorViews";
import StreamIntelligencePanel from "../components/StreamIntelligencePanel";
import EvidenceCards from "../components/EvidenceCards";
import PartnerPdfButton from "../components/PartnerPdfButton";

const CADENCES = [
    { value: "manual", label: "Manual only" },
    { value: "hourly", label: "Hourly" },
    { value: "every_6h", label: "Every 6 hours" },
    { value: "every_12h", label: "Every 12 hours" },
    { value: "daily", label: "Daily" },
    { value: "every_2_days", label: "Every 2 days" },
    { value: "weekly", label: "Weekly (default — saves Apify credits)" },
    { value: "monthly", label: "Monthly" },
];

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
    } catch {
        return "";
    }
}

function fmtDateTime(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
    } catch {
        return "";
    }
}

function VelocityChart({ points }) {
    if (!points || points.length === 0) {
        return <p className="text-sm text-muted-foreground italic">Velocity will plot once signals accumulate over a few days.</p>;
    }
    return (
        <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={points} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="day" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} allowDecimals={false} />
                <Tooltip contentStyle={{ backgroundColor: "hsl(var(--popover))", borderColor: "hsl(var(--border))", fontSize: 12 }} />
                <Area type="monotone" dataKey="count" stroke="hsl(var(--primary))" fill="hsl(var(--primary) / 0.18)" strokeWidth={2} />
            </AreaChart>
        </ResponsiveContainer>
    );
}

function EditDialog({ stream, onSaved }) {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(null);

    // Initialize form ONLY when the dialog opens. Don't re-init when the parent
    // refetches `stream` (10s polling), or in-flight edits get clobbered.
    useEffect(() => {
        if (open && stream && form === null) {
            setForm({
                cadence: stream.cadence,
                active: stream.active,
                topics: (stream.topics || []).join(", "),
                platforms: stream.platforms || [],
                keywords: (stream.keywords || []).join(", "),
                subreddits: (stream.subreddits || []).join(", "),
                tiktok_search_terms: (stream.tiktok_search_terms || []).join(", "),
                topic_tags: (stream.topic_tags || []).join(", "),
                description: stream.description || "",
                max_items_per_run: stream.max_items_per_run || 60,
            });
        }
        // Reset form back to null when dialog closes so next open re-seeds from latest stream.
        if (!open && form !== null) {
            setForm(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, stream]);

    const togglePlatform = (p) => {
        if (!form) return;
        const next = form.platforms.includes(p)
            ? form.platforms.filter((x) => x !== p)
            : [...form.platforms, p];
        setForm({ ...form, platforms: next });
    };

    const save = async () => {
        const payload = {
            description: form.description,
            topics: form.topics.split(",").map((s) => s.trim()).filter(Boolean),
            platforms: form.platforms,
            keywords: form.keywords.split(",").map((s) => s.trim()).filter(Boolean),
            subreddits: form.subreddits.split(",").map((s) => s.trim()).filter(Boolean),
            tiktok_search_terms: form.tiktok_search_terms.split(",").map((s) => s.trim()).filter(Boolean),
            topic_tags: form.topic_tags.split(",").map((s) => s.trim()).filter(Boolean),
            cadence: form.cadence,
            active: form.active,
            max_items_per_run: Number(form.max_items_per_run) || 60,
        };
        try {
            const r = await api.patch(`/streams/${stream.id}`, payload);
            toast.success("Stream updated");
            setOpen(false);
            onSaved?.(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Update failed");
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <Button data-testid="edit-stream-btn" variant="outline" size="sm" className="rounded-sm h-8" onClick={() => setOpen(true)}>
                <GearSix size={13} className="mr-1.5" /> Configure
            </Button>
            <DialogContent className="rounded-sm max-w-lg max-h-[88vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-display">Configure stream</DialogTitle></DialogHeader>
                {form && (
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs">Description</Label>
                            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm" />
                        </div>
                        <div>
                            <Label className="text-xs">Topics (comma-separated)</Label>
                            <Input value={form.topics} onChange={(e) => setForm({ ...form, topics: e.target.value })} placeholder="dating burnout, swipe fatigue" className="rounded-sm" />
                        </div>
                        <div>
                            <Label className="text-xs mb-2 block">Platforms to search</Label>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                {["Reddit","YouTube","TikTok","Instagram"].map((p) => {
                                    const active = form.platforms.includes(p);
                                    return (
                                        <button
                                            key={p}
                                            type="button"
                                            onClick={() => togglePlatform(p)}
                                            className={`p-2 border rounded-sm text-xs font-medium ${active ? "border-primary bg-primary/5 text-foreground" : "border-border text-muted-foreground"}`}
                                        >
                                            {p}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs">Pinned subreddits (advanced)</Label>
                            <Input value={form.subreddits} onChange={(e) => setForm({ ...form, subreddits: e.target.value })} className="rounded-sm font-mono text-xs" />
                        </div>
                        <div>
                            <Label className="text-xs">Keyword filter (optional)</Label>
                            <Input value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} className="rounded-sm" />
                        </div>
                        <div>
                            <Label className="text-xs">Topic tags</Label>
                            <Input value={form.topic_tags} onChange={(e) => setForm({ ...form, topic_tags: e.target.value })} className="rounded-sm" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs">Cadence</Label>
                                <Select value={form.cadence} onValueChange={(v) => setForm({ ...form, cadence: v })}>
                                    <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {CADENCES.map((c) => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs">Max items / run</Label>
                                <Input type="number" value={form.max_items_per_run} onChange={(e) => setForm({ ...form, max_items_per_run: e.target.value })} className="rounded-sm" />
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
                            <Label className="text-xs">Active</Label>
                        </div>
                    </div>
                )}
                <DialogFooter>
                    <Button data-testid="save-stream-btn" onClick={save} className="rounded-sm">Save</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default function StreamDetail() {
    const { id } = useParams();
    const [intel, setIntel] = useState(null);
    const [scope, setScope] = useState(null);
    const [synthesizing, setSynthesizing] = useState(false);
    const [recentSignals, setRecentSignals] = useState([]);

    const load = async () => {
        const [i, s, c] = await Promise.all([
            api.get(`/streams/${id}/intelligence`).then((r) => r.data).catch(() => null),
            api.get(`/streams/${id}/discovery-scope`).then((r) => r.data).catch(() => null),
            api.get("/comments", { params: { stream_id: id, limit: 20 } }).then((r) => r.data).catch(() => []),
        ]);
        setIntel(i);
        setScope(s);
        setRecentSignals(c);
    };

    useEffect(() => {
        load();
        const t = setInterval(load, 10000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const stream = intel?.stream;

    const ingest = async () => {
        try {
            await api.post(`/streams/${id}/ingest`);
            toast.success("Ingestion started");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not start ingestion");
        }
    };

    const synthesize = async () => {
        setSynthesizing(true);
        try {
            await api.post(`/streams/${id}/synthesize`, { auto_analyze: true, max_comments: 60 });
            toast.success("Narrative briefing synthesized");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Synthesis failed");
        } finally {
            setSynthesizing(false);
        }
    };

    const togglePause = async () => {
        if (!stream) return;
        try {
            const r = await api.patch(`/streams/${id}`, { active: !stream.active });
            toast.success(r.data.active ? "Stream activated" : "Stream paused");
            await load();
        } catch {
            toast.error("Could not toggle stream");
        }
    };

    const remove = async () => {
        if (!window.confirm("Delete this stream? Comments will be retained but unlinked.")) return;
        await api.delete(`/streams/${id}`);
        toast.success("Stream deleted");
        window.location.href = "/streams";
    };

    if (!stream) {
        return (
            <PageBody>
                <div className="text-sm text-muted-foreground">Loading…</div>
            </PageBody>
        );
    }

    const briefing = stream.last_briefing;
    const accel = intel?.velocity?.acceleration ?? 0;
    const accelPct = (accel * 100).toFixed(0);

    const headline = briefing?.headline || stream.name;
    const lead = briefing?.lead || stream.description || "Synthesize a briefing to read this stream's editorial summary.";
    const tensions = briefing?.tensions || (intel?.top_contradictions || []).map((x) => x.label);
    const verbatim = briefing?.verbatim || (intel?.top_repeated_phrases || []).map((x) => x.label);
    const quietly = briefing?.quietly_true || (intel?.cultural_observations || []).map((x) => ({ observation: x.label, elaboration: "" }));
    const rising = briefing?.rising_vocabulary || (intel?.emerging_language || []).map((x) => x.label);
    const felt = briefing?.felt_experience || (intel?.top_emotional_themes || []).map((x) => x.label);

    return (
        <>
            {/* Editorial header strip */}
            <header data-testid="stream-header" className="px-8 lg:px-14 pt-10 pb-6 border-b border-border animate-fade-up">
                <Link to="/streams" data-testid="back-to-streams" className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground hover:text-primary mb-5">
                    <ArrowLeft size={11} /> All streams
                </Link>
                <div className="flex items-end justify-between gap-6 flex-wrap">
                    <div>
                        <div className="overline mb-2">
                            Narrative Stream · {fmtDate(briefing?.generated_at || stream.last_ingested_at || stream.created_at)}
                        </div>
                        <h1 data-testid="stream-name" className="font-display font-bold text-3xl md:text-4xl tracking-tight leading-[1.05]">
                            {stream.name}
                        </h1>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button
                            data-testid="ingest-now-btn"
                            size="sm"
                            variant="outline"
                            className="rounded-sm h-8"
                            onClick={ingest}
                            disabled={stream.last_status === "ingesting" || !stream.active}
                            title={!stream.active ? "Stream is paused. Click Activate to resume." : ""}
                        >
                            {stream.last_status === "ingesting"
                                ? <ArrowsClockwise size={13} className="mr-1.5 animate-spin" />
                                : <Pulse size={13} className="mr-1.5" />}
                            {stream.last_status === "ingesting" ? "Ingesting…" : "Ingest now"}
                        </Button>
                        <Button
                            data-testid="synthesize-stream-btn"
                            size="sm"
                            className="rounded-sm h-8"
                            onClick={synthesize}
                            disabled={synthesizing}
                        >
                            {synthesizing
                                ? <ArrowsClockwise size={13} className="mr-1.5 animate-spin" />
                                : <Sparkle size={13} weight="duotone" className="mr-1.5" />}
                            {briefing ? "Re-synthesize" : "Synthesize briefing"}
                        </Button>
                        <Button data-testid="toggle-pause-btn" size="sm" variant="ghost" className="rounded-sm h-8" onClick={togglePause}>
                            {stream.active ? <><Pause size={13} className="mr-1.5" /> Pause</> : <><Play size={13} className="mr-1.5" /> Activate</>}
                        </Button>
                        <PartnerPdfButton streamId={stream.id} streamName={stream.name} variant="prominent" />
                        <EditDialog stream={stream} onSaved={load} />
                        <Button data-testid="delete-stream-btn" size="sm" variant="ghost" className="rounded-sm h-8" onClick={remove}>
                            <Trash size={13} />
                        </Button>
                    </div>
                </div>
            </header>

            <PageBody>
                {/* Error banner — surfaces last_error prominently when ingest failed */}
                <div className="max-w-[1100px] mx-auto pt-4">
                    <StreamErrorBanner stream={stream} onRetried={load} />
                </div>

                {/* Telemetry strip — 4 small editorial cells, not a KPI grid */}
                <section data-testid="stream-telemetry" className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12 pt-2 max-w-[1100px] mx-auto">
                    <div>
                        <div className="overline mb-1.5">Signals</div>
                        <div className="font-display font-bold text-3xl tabular-nums">{stream.signal_count || 0}</div>
                        <div className="text-[10px] font-mono text-muted-foreground mt-1">collected</div>
                    </div>
                    <div>
                        <div className="overline mb-1.5">Acceleration</div>
                        <div className="font-display font-bold text-2xl leading-none">
                            {intel?.velocity?.acceleration_label || "—"}
                        </div>
                        <div className="text-[10px] font-mono text-muted-foreground mt-1.5">
                            {intel?.velocity?.acceleration_multiplier > 0
                                ? `${intel.velocity.acceleration_multiplier}× narrative frequency WoW`
                                : "WoW frequency"}
                        </div>
                    </div>
                    <div data-testid="stream-archetypes">
                        <div className="overline mb-1.5">Archetypes</div>
                        {(intel?.archetypes || []).length === 0 ? (
                            <div className="text-[13px] italic text-muted-foreground mt-1">awaiting more signal</div>
                        ) : (
                            <div className="flex flex-wrap gap-1.5 mt-1">
                                {(intel?.archetypes || []).map((a) => (
                                    <span
                                        key={a.name}
                                        className="font-display font-medium text-[12px] tracking-tight px-2 py-1 bg-foreground text-background rounded-sm"
                                        title={`${a.evidence_hits} marker matches in recent analyses & signals`}
                                    >
                                        {a.name}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                    <div>
                        <div className="overline mb-1.5">Last ingested</div>
                        <div className="font-display font-medium text-base leading-tight">{fmtDateTime(stream.last_ingested_at)}</div>
                        <div className="text-[10px] font-mono text-muted-foreground mt-1">
                            next: {fmtDateTime(stream.next_run_at)}
                        </div>
                    </div>
                </section>

                {/* DISCOVERY SCOPE — editorial transparency: exactly what we're searching for, and where */}
                {scope && (
                    <section data-testid="discovery-scope" className="mb-12 max-w-[1100px] mx-auto border border-border bg-card px-7 py-6">
                        <div className="flex items-baseline gap-3 mb-4">
                            <div className="overline">Current discovery scope</div>
                            <div className="h-px bg-border flex-1" />
                            <span className="font-mono text-[10px] text-muted-foreground tracking-wider">
                                {scope.display_state}
                            </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6">
                            <div>
                                <div className="overline text-[9px] mb-2">Platforms</div>
                                <div className="flex flex-wrap gap-2">
                                    {(scope.platforms || []).map((p) => (
                                        <span key={p} className="font-mono text-[11px] tracking-wider px-2 py-0.5 border border-border rounded-sm">{p}</span>
                                    ))}
                                    {(scope.platforms || []).length === 0 && (
                                        <span className="text-[11px] text-muted-foreground italic">No platforms enabled.</span>
                                    )}
                                </div>
                            </div>
                            <div>
                                <div className="overline text-[9px] mb-2">Cadence</div>
                                <div className="font-mono text-[11px]">
                                    {scope.cadence}
                                    {scope.next_run_at && <> · next {fmtDateTime(scope.next_run_at)}</>}
                                </div>
                            </div>
                            {(scope.search_terms || []).length > 0 && (
                                <div className="md:col-span-2">
                                    <div className="overline text-[9px] mb-2">Searching for</div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {scope.search_terms.slice(0, 14).map((t, i) => (
                                            <span key={i} className="text-[11px] bg-primary/10 text-primary px-2 py-0.5 rounded-sm">{t}</span>
                                        ))}
                                        {scope.search_terms.length > 14 && (
                                            <span className="text-[11px] text-muted-foreground self-center">+{scope.search_terms.length - 14}</span>
                                        )}
                                    </div>
                                </div>
                            )}
                            <div>
                                <EditableChips
                                    streamId={id}
                                    field="suggested_communities"
                                    value={scope.suggested_communities || []}
                                    label="Suggested communities"
                                    placeholder="r/dating, /r/relationships…"
                                    onSaved={() => load()}
                                    testid="chips-communities"
                                />
                            </div>
                            <div>
                                <EditableChips
                                    streamId={id}
                                    field="suggested_creators"
                                    value={scope.suggested_creators || []}
                                    label="Suggested creators"
                                    placeholder="@handle, channel name…"
                                    onSaved={() => load()}
                                    testid="chips-creators"
                                    prefix="@"
                                />
                            </div>
                            <div>
                                <EditableChips
                                    streamId={id}
                                    field="suggested_hashtags"
                                    value={scope.suggested_hashtags || []}
                                    label="Suggested hashtags"
                                    placeholder="datingfatigue, situationship…"
                                    onSaved={() => load()}
                                    testid="chips-hashtags"
                                    prefix="#"
                                />
                            </div>
                            <div>
                                <EditableChips
                                    streamId={id}
                                    field="suggested_search_phrases"
                                    value={scope.suggested_search_phrases || []}
                                    label="Suggested search phrases"
                                    placeholder="why is dating so exhausting…"
                                    onSaved={() => load()}
                                    testid="chips-phrases"
                                />
                            </div>
                        </div>
                        <p className="mt-5 pt-4 border-t border-border text-[11px] text-muted-foreground leading-relaxed italic">
                            {scope.disclaimer}
                        </p>
                    </section>
                )}

                <article className="max-w-[1100px] mx-auto">
                    {/* HERO */}
                    <section data-testid="stream-hero" className="mb-14 grid grid-cols-12 gap-6 lg:gap-10">
                        <div className="col-span-12 lg:col-span-9">
                            {briefing && (
                                <div className="overline mb-3 text-primary">Editorial briefing · {(briefing.model_used || "").split(":")[0]}</div>
                            )}
                            <h2
                                className="font-display font-black tracking-[-0.025em] leading-[1.0] text-[clamp(2rem,4vw,4.2rem)]"
                                data-testid="stream-headline"
                            >
                                {headline}
                            </h2>
                            {lead && (
                                <p
                                    data-testid="stream-lead"
                                    className={`editorial-lead mt-7 text-[18px] md:text-[20px] text-foreground/90 max-w-[68ch] ${briefing ? "drop-cap" : ""}`}
                                >
                                    {lead}
                                </p>
                            )}
                        </div>
                        <aside className="col-span-12 lg:col-span-3 lg:border-l lg:border-border lg:pl-6 flex flex-col justify-end">
                            <div className="overline mb-3">Felt experience</div>
                            {felt.length === 0 ? (
                                <p className="text-sm text-muted-foreground italic">No themes yet.</p>
                            ) : (
                                <ul className="space-y-1.5">
                                    {felt.slice(0, 6).map((t, i) => (
                                        <li key={i} className="font-display font-semibold text-base leading-tight">
                                            <span className="text-muted-foreground/40 font-mono text-xs mr-2">{String(i + 1).padStart(2, "0")}</span>
                                            {t}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </aside>
                    </section>

                    {/* INTELLIGENCE PANEL — replaces velocity chart with proprietary cultural metrics */}
                    <StreamIntelligencePanel metrics={intel?.metrics} velocity={intel?.velocity} />

                    {/* TENSIONS */}
                    {tensions.length > 0 && (
                        <section data-testid="stream-tensions" className="mb-14">
                            <div className="flex items-baseline gap-3 mb-7">
                                <div className="overline">Tensions</div>
                                <div className="h-px bg-border flex-1" />
                                <span className="font-mono text-[11px] text-muted-foreground">{tensions.length.toString().padStart(2, "0")}</span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-7">
                                {tensions.slice(0, 6).map((t, i) => {
                                    const split = t.match(/^(.+?)\s+(but|yet|and yet|—\s*and yet\s*—|—|while|whereas|—but|—\s*but|\/)\s+(.+)$/i);
                                    return (
                                        <div key={i} className="border-l-2 border-accent pl-5">
                                            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-2">
                                                Tension · {String(i + 1).padStart(2, "0")}
                                            </div>
                                            {split ? (
                                                <p className="editorial text-[18px] leading-snug">
                                                    <span>{split[1].trim()}</span>
                                                    <span className="text-accent italic font-normal mx-2">/ but /</span>
                                                    <span>{split[3].trim()}</span>
                                                </p>
                                            ) : (
                                                <p className="editorial text-[18px] leading-snug">{t}</p>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </section>
                    )}

                    {/* VERBATIM */}
                    {verbatim.length > 0 && (
                        <section data-testid="stream-verbatim" className="mb-14">
                            <div className="flex items-baseline gap-3 mb-7">
                                <div className="overline">Verbatim · what they keep saying</div>
                                <div className="h-px bg-border flex-1" />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {verbatim.slice(0, 6).map((q, i) => (
                                    <blockquote key={i} className="relative pl-10 pr-4 py-3">
                                        <Quotes size={26} weight="fill" className="absolute left-0 top-1 text-primary/15" />
                                        <p className="editorial italic text-[17px] leading-snug text-foreground">
                                            {q.replace(/^["']|["']$/g, "")}
                                        </p>
                                    </blockquote>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* QUIETLY TRUE */}
                    {quietly.length > 0 && (
                        <section className="mb-14">
                            <div className="flex items-baseline gap-3 mb-7">
                                <div className="overline">What's quietly true</div>
                                <div className="h-px bg-border flex-1" />
                            </div>
                            <div className="space-y-8 max-w-[68ch]">
                                {quietly.slice(0, 4).map((o, i) => (
                                    <div key={i}>
                                        <div className="flex items-baseline gap-3 mb-2">
                                            <span className="font-mono text-[11px] text-primary tabular-nums">№ {String(i + 1).padStart(2, "0")}</span>
                                            <div className="h-px bg-border flex-1 opacity-60" />
                                        </div>
                                        <h3 className="font-display font-bold text-xl tracking-tight leading-[1.2] mb-2">{o.observation}</h3>
                                        {o.elaboration && (
                                            <p className="editorial text-[15px] text-muted-foreground leading-relaxed">{o.elaboration}</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* RISING VOCABULARY */}
                    {rising.length > 0 && (
                        <section className="mb-12 pt-6 border-t border-border">
                            <div className="overline mb-4">Rising vocabulary</div>
                            <ul className="flex flex-wrap gap-x-4 gap-y-2">
                                {rising.slice(0, 12).map((r, i) => (
                                    <li key={i} className="font-display font-medium text-base leading-tight border-b border-border pb-0.5 hover:border-primary transition-colors">
                                        {r}
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    {/* MONITORING CONFIG (read-only summary) */}
                    <section className="mb-14 pt-6 border-t border-border">
                        <div className="overline mb-4">Listening for</div>
                        {(stream.topics || []).length > 0 ? (
                            <div className="flex flex-wrap gap-1.5 mb-5">
                                {(stream.topics || []).map((t) => (
                                    <span key={t} className="inline-flex items-center gap-1.5 text-[12px] bg-primary/10 text-primary px-2.5 py-1 rounded-sm font-medium">
                                        <span className="font-mono text-[10px]">⌕</span>{t}
                                    </span>
                                ))}
                            </div>
                        ) : (
                            <p className="text-xs italic text-muted-foreground mb-5">No topics configured.</p>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                            <div>
                                <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-2">Platforms searched</div>
                                <div className="flex flex-wrap gap-1.5">
                                    {(stream.platforms || []).length === 0 && (!stream.subreddits || stream.subreddits.length === 0) && (
                                        <span className="text-xs italic text-muted-foreground">None — add platforms in Configure.</span>
                                    )}
                                    {(stream.platforms || []).map((p) => (
                                        <span key={p} className="font-mono text-[11px] px-2 py-0.5 border border-primary/40 text-primary rounded-sm">{p}</span>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-2">Pinned subreddits</div>
                                <div className="flex flex-wrap gap-1.5">
                                    {(stream.subreddits || []).map((s) => (<span key={s} className="font-mono text-[11px] px-2 py-0.5 bg-secondary rounded-sm">{s}</span>))}
                                    {(!stream.subreddits || stream.subreddits.length === 0) && <span className="text-xs italic text-muted-foreground">None</span>}
                                </div>
                            </div>
                            <div>
                                <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-2">Keyword filter</div>
                                <div className="flex flex-wrap gap-1.5">
                                    {(stream.keywords || []).map((k) => (<span key={k} className="font-mono text-[11px] px-2 py-0.5 border border-border rounded-sm italic">"{k}"</span>))}
                                    {(!stream.keywords || stream.keywords.length === 0) && <span className="text-xs italic text-muted-foreground">All comments accepted</span>}
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* EVIDENCE CARDS — replaces raw signal dump */}
                    <EvidenceCards
                        evidenceCards={intel?.evidence_cards}
                        recentSignals={recentSignals}
                        streamId={id}
                    />
                </article>
            </PageBody>
        </>
    );
}
